// utils/seedAdminLogs.js
// Run this once to create initial log entries for existing bugs/enhancements
import { createLogEntry, updateLogEntry, addLogNote } from "./adminLogs";

/**
 * Seed initial bug/enhancement entries
 * Run this function once manually or on app initialization
 */
export async function seedInitialLogs() {
  try {
    // Bug: Player.js crashes on mobile devices
    await createLogEntry({
      type: "bug",
      title: "Player.js crashes on mobile devices (Samsung/Android)",
      description: `Player UI crashes randomly on smartphones, especially Samsung phones and sometimes Android/Google phones. Crashes are random, can happen at any time, no specific question type. May be related to device timeout settings or memory management issues.`,
      status: "current",
      priority: "high",
      tags: ["crash", "player", "mobile", "android", "samsung"],
      createdBy: "conversation-2025-01-29",
    });

    // Bug: Duplicate onSnapshot listeners (RESOLVED)
    const entry1 = await createLogEntry({
      type: "bug",
      title: "Duplicate onSnapshot listeners causing memory leaks",
      description: `Player.js had duplicate onSnapshot listeners for points (lines 513-547 and 686-705), causing memory leaks and potential crashes on mobile devices.`,
      status: "resolved",
      priority: "high",
      tags: ["memory-leak", "player", "firebase", "listeners"],
      createdBy: "conversation-2025-01-29",
    });
    await updateLogEntry(entry1.id, {
      resolvedInVersion: "v2.1.0",
    });
    await addLogNote(entry1.id, "Removed duplicate listener at lines 686-705, kept the version with questionOpenedAtRef freshness checks.");

    // Bug: Missing error handling (RESOLVED)
    const entry2 = await createLogEntry({
      type: "bug",
      title: "Missing error handling in Firebase onSnapshot calls",
      description: `All Firebase onSnapshot calls in Player.js lacked error handlers, causing silent failures and potential crashes on network errors.`,
      status: "resolved",
      priority: "high",
      tags: ["error-handling", "firebase", "player", "network"],
      createdBy: "conversation-2025-01-29",
    });
    await updateLogEntry(entry2.id, {
      resolvedInVersion: "v2.1.0",
    });
    await addLogNote(entry2.id, "Added error callbacks to all onSnapshot listeners with console.error logging.");

    // Bug: MULTIANSWER selection issues (RESOLVED)
    const entry3 = await createLogEntry({
      type: "bug",
      title: "MULTIANSWER questions - cannot select answers randomly",
      description: `For some MULTIANSWER questions, players couldn't select answers. Issue was missing maxSelectable derivation from bracketed correct answers.`,
      status: "resolved",
      priority: "medium",
      tags: ["multianswer", "selection", "player"],
      createdBy: "conversation-2025-01-29",
    });
    await updateLogEntry(entry3.id, {
      resolvedInVersion: "v2.1.0",
    });
    await addLogNote(entry3.id, "Added auto-derivation of maxSelectable from number of bracketed correct answers when not provided.");

    // Enhancement: Reconnection System (COMPLETED)
    const entry4 = await createLogEntry({
      type: "enhancement",
      title: "Player reconnection system with state persistence",
      description: `Implement automatic and manual reconnection for players who crash or lose connection. Save team name, gameCode, and selfieUrl to localStorage. Auto-restore on app restart. Add manual reconnect button on PlayerHome. Restore existing answers and scores from Firebase.`,
      status: "completed",
      priority: "high",
      tags: ["reconnection", "player", "persistence", "localStorage", "firebase"],
      createdBy: "conversation-2025-01-29",
    });
    await updateLogEntry(entry4.id, {
      resolvedInVersion: "v2.1.0",
    });
    await addLogNote(entry4.id, "Implemented: Auto-restore on mount, manual reconnect UI in PlayerHome, score restoration from Firebase, state persistence in localStorage.");

    // Enhancement: Keyboard shortcut for options (COMPLETED)
    const entry5 = await createLogEntry({
      type: "enhancement",
      title: "Keyboard shortcut 'o' to open Quiz Options popup",
      description: `Add keyboard shortcut 'o' on host screen to open the existing Quiz Options popup (same one that appears on slide 0).`,
      status: "completed",
      priority: "low",
      tags: ["keyboard-shortcut", "host", "ui"],
      createdBy: "conversation-2025-01-29",
    });
    await updateLogEntry(entry5.id, {
      resolvedInVersion: "v2.1.0",
    });
    await addLogNote(entry5.id, "Added 'o' key handler in Quizmaster.js that writes to Firebase, Quizscreen.js listens and opens OptionsPanel.");

    // Enhancement: Bug/Enhancement Tracking System (COMPLETED)
    await createLogEntry({
      type: "enhancement",
      title: "Bug and Enhancement Tracking System",
      description: `Create comprehensive bug/enhancement log system stored in Firestore, accessible only to Qwizard admins on About page. Features: auto-numbering (bug-001, enh-001), status tracking, two-column display, filtering, notes, version tracking.`,
      status: "completed",
      priority: "medium",
      tags: ["admin", "tracking", "about-page", "firestore"],
      createdBy: "conversation-2025-01-29",
    });

    console.log("✅ Initial log entries created successfully");
  } catch (err) {
    console.error("❌ Error seeding initial logs:", err);
    throw err;
  }
}

// Export a helper to check if logs need seeding
export async function shouldSeedLogs() {
  try {
    const { getAllLogs } = await import("./adminLogs");
    const logs = await getAllLogs();
    return logs.length === 0;
  } catch {
    return false;
  }
}
